import { Button } from '@mui/material';
import { DateTime } from 'luxon';
import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import type { Company } from '@core/types/api/Company/Company';
import type { Contact } from '@core/types/api/Contact/Contact';
import type { Project } from '@core/types/api/Project/Project';
import type { Quote } from '@core/types/api/Quote/Quote';
import { ActivityDrawer } from '@web/components/ActivityDrawer/ActivityDrawer';
import { ServerErrorMessage } from '@web/hooks/useSlabQuery';
import type { QueryError } from '@web/utils/Query';

import { TOAST_CONFIG } from '../../constants/app';
import { CompanyDrawer } from '../../pages/Companies/CompanyDrawer';
import { ContactDrawer } from '../../pages/Contacts/ContactDrawer';
import { ProjectDrawer } from '../../pages/Projects/ProjectDrawer/ProjectDrawer';
import { QuoteDrawer, QuoteDrawerMode } from '../../pages/Quotes/QuotesDrawer/QuoteDrawer';
import { makeDefaultFormikQuote } from '../../pages/Quotes/QuotesDrawer/utils';
import { useToast } from '../ToastProvider/useToast';

type QuickActionsContextValue = {
  openProjectDrawer: () => void;
  openCompanyDrawer: () => void;
  openContactDrawer: () => void;
  openActivityDrawer: () => void;
  openQuoteDrawer: () => void;
};

const QuickActionsContext = createContext<QuickActionsContextValue | null>(null);

export const useQuickActions = (): QuickActionsContextValue => {
  const ctx = useContext(QuickActionsContext);
  if (ctx === null) {
    throw new Error('useQuickActions must be used within QuickActionsProvider');
  }
  return ctx;
};

type Props = { children: ReactNode };

const noop = (): void => {};

export const QuickActionsProvider: FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const toastHook = useToast();

  const [projectOpen, setProjectOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const [projectMounted, setProjectMounted] = useState(false);
  const [companyMounted, setCompanyMounted] = useState(false);
  const [contactMounted, setContactMounted] = useState(false);
  const [activityMounted, setActivityMounted] = useState(false);
  const [quoteMounted, setQuoteMounted] = useState(false);

  const openProjectDrawer = useCallback(() => {
    setProjectMounted(true);
    setProjectOpen(true);
  }, []);
  const openCompanyDrawer = useCallback(() => {
    setCompanyMounted(true);
    setCompanyOpen(true);
  }, []);
  const openContactDrawer = useCallback(() => {
    setContactMounted(true);
    setContactOpen(true);
  }, []);
  const openActivityDrawer = useCallback(() => {
    setActivityMounted(true);
    setActivityOpen(true);
  }, []);
  const openQuoteDrawer = useCallback(() => {
    setQuoteMounted(true);
    setQuoteOpen(true);
  }, []);

  const handleProjectSuccess = useCallback(
    (project: Project) => {
      setProjectOpen(false);
      toastHook.success({
        title: 'Project created',
        action: (
          <Button size='small' onClick={() => navigate(`/projects/${project.id}`)}>
            View
          </Button>
        ),
        config: TOAST_CONFIG,
      });
    },
    [toastHook, navigate],
  );

  const handleCompanySave = useCallback(
    (company: Company) => {
      setCompanyOpen(false);
      toastHook.success({
        title: 'Company created',
        action: (
          <Button size='small' onClick={() => navigate(`/companies/${company.id}`)}>
            View
          </Button>
        ),
        config: TOAST_CONFIG,
      });
    },
    [toastHook, navigate],
  );

  const handleContactSave = useCallback(
    (contact: Contact) => {
      setContactOpen(false);
      toastHook.success({
        title: 'Contact created',
        action: (
          <Button size='small' onClick={() => navigate(`/contacts/${contact.id}`)}>
            View
          </Button>
        ),
        config: TOAST_CONFIG,
      });
    },
    [toastHook, navigate],
  );

  const handleActivitySuccess = useCallback(() => {
    setActivityOpen(false);
    toastHook.success({
      title: 'Activity saved',
      config: TOAST_CONFIG,
    });
  }, [toastHook]);

  const handleQuoteSuccess = useCallback(
    (quote: Quote) => {
      setQuoteOpen(false);
      toastHook.success({
        title: 'Quote created',
        config: TOAST_CONFIG,
      });
      navigate(`/quotes/${quote.id}`);
    },
    [toastHook, navigate],
  );

  const handleQuoteError = useCallback(
    (error: unknown, defaultErrorMessage?: string) => {
      toastHook.error({
        message: ServerErrorMessage(error, defaultErrorMessage),
      });
    },
    [toastHook],
  );

  const handleError = useCallback(
    (error: QueryError) => {
      toastHook.error({ message: ServerErrorMessage(error) });
    },
    [toastHook],
  );

  const value = useMemo<QuickActionsContextValue>(
    () => ({
      openProjectDrawer,
      openCompanyDrawer,
      openContactDrawer,
      openActivityDrawer,
      openQuoteDrawer,
    }),
    [openProjectDrawer, openCompanyDrawer, openContactDrawer, openActivityDrawer, openQuoteDrawer],
  );

  return (
    <QuickActionsContext.Provider value={value}>
      {children}

      {projectMounted && (
        <ProjectDrawer
          projectID={null}
          isOpen={projectOpen}
          setIsOpen={setProjectOpen}
          onSuccess={handleProjectSuccess}
          onError={handleError}
        />
      )}

      {companyMounted && (
        <CompanyDrawer
          resourceID={null}
          isOpen={companyOpen}
          setIsOpen={setCompanyOpen}
          onSave={handleCompanySave}
          onError={handleError}
        />
      )}

      {contactMounted && (
        <ContactDrawer
          resourceID={null}
          isOpen={contactOpen}
          setIsOpen={setContactOpen}
          onSave={handleContactSave}
          onError={handleError}
        />
      )}

      {activityMounted && (
        <ActivityDrawer
          activityID={null}
          currentDate={DateTime.now()}
          isOpen={activityOpen}
          setIsOpen={setActivityOpen}
          onSuccess={handleActivitySuccess}
          onError={handleError}
        />
      )}

      {quoteMounted && (
        <QuoteDrawer
          mode={QuoteDrawerMode.Quick}
          resourceID={null}
          isOpen={quoteOpen}
          setIsOpen={setQuoteOpen}
          submitType='save'
          makeDefaultFormikQuote={makeDefaultFormikQuote}
          onRequestSuccess={handleQuoteSuccess}
          onRequestError={handleQuoteError}
          onNylasSuccess={noop}
          onNylasError={(message) => toastHook.error({ message })}
        />
      )}
    </QuickActionsContext.Provider>
  );
};
